// API Service for Admin frontend
const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://printformee.com/api";

const getAuthToken = () =>
  localStorage.getItem("adminToken") || localStorage.getItem("token");

const clearAuthData = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("adminToken");
  localStorage.removeItem("user");
  localStorage.removeItem("adminUser");
};

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Generic request method with error handling
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;

    // Get auth token from localStorage with fallback support for adminToken
    const token =
      localStorage.getItem("adminToken") || localStorage.getItem("token");

    const config = {
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);

      // Handle 401 Unauthorized - token expired or invalid
      if (response.status === 401) {
        console.log("🔐 Authentication failed - clearing expired token");
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        // Trigger a page reload to redirect to login
        window.location.href = "/login";
        throw new Error("Authentication expired. Please login again.");
      }

      if (!response.ok) {
        throw new Error(
          `API Error: ${response.status} - ${response.statusText}`,
        );
      }

      return await response.json();
    } catch (error) {
      console.error("API request failed:", error);
      throw error;
    }
  }

  // Get product by ID
  async getProduct(id) {
    try {
      console.log("🔍 Fetching product with ID:", id);
      const response = await this.request(`/products/${id}`);
      console.log("🔍 Raw product response:", response);
      console.log("🔍 Response type:", typeof response);
      console.log("🔍 Response keys:", Object.keys(response));

      // The backend returns { success: true, data: { product } }
      const product = response.data || response.product || response;
      console.log("🔍 Extracted product:", product);
      return product;
    } catch (error) {
      console.error("Failed to fetch product:", error);
      throw error;
    }
  }

  // Get all products
  async getProducts() {
    try {
      console.log("Making API request to /products...");
      const response = await this.request("/products");
      console.log("Raw API response:", response);
      // Handle different response formats
      const products = Array.isArray(response)
        ? response
        : response.data || response.products || [];
      console.log("Extracted products:", products);
      console.log("Products length:", products.length);
      return products;
    } catch (error) {
      console.error("Failed to fetch products:", error);
      throw error;
    }
  }

  // Create product
  async createProduct(productData) {
    try {
      const response = await this.request("/products", {
        method: "POST",
        body: JSON.stringify(productData),
      });
      return response.product || response;
    } catch (error) {
      console.error("Failed to create product:", error);
      throw error;
    }
  }

  // Update product
  async updateProduct(id, productData) {
    try {
      const response = await this.request(`/products/${id}`, {
        method: "PUT",
        body: JSON.stringify(productData),
      });
      return response.product || response;
    } catch (error) {
      console.error("Failed to update product:", error);
      throw error;
    }
  }

  // Delete product
  async deleteProduct(id) {
    try {
      const response = await this.request(`/products/${id}`, {
        method: "DELETE",
      });
      return response;
    } catch (error) {
      console.error("Failed to delete product:", error);
      throw error;
    }
  }

  // Toggle product featured status
  async toggleProductFeatured(id) {
    try {
      const response = await this.request(`/products/${id}/toggle-featured`, {
        method: "PUT",
      });
      return response;
    } catch (error) {
      console.error("Failed to toggle featured:", error);
      throw error;
    }
  }

  // Get categories
  async getCategories() {
    try {
      const response = await this.request("/categories");
      return response.categories || response.data || response || [];
    } catch (error) {
      console.error("Failed to fetch categories:", error);
      throw error;
    }
  }

  // Get brands
  async getBrands() {
    try {
      const response = await this.request("/products/brands");
      return response.brands || response || [];
    } catch (error) {
      console.error("Failed to fetch brands:", error);
      throw error;
    }
  }

  // Create product with files
  async createProductWithFiles(productData, files) {
    try {
      const formData = new FormData();

      // Add product data as JSON string
      formData.append("productData", JSON.stringify(productData));

      // Add files if they exist
      if (files.thumbnail) {
        formData.append("thumbnail", files.thumbnail);
      }

      if (files.additional_images && files.additional_images.length > 0) {
        files.additional_images.forEach((file) => {
          formData.append("images", file);
        });
      }

      if (files.meta_image) {
        formData.append("meta_image", files.meta_image);
      }

      // Add color images if they exist
      if (files.color_images) {
        console.log(
          "DEBUG - color_images found:",
          Object.keys(files.color_images),
        );
        Object.keys(files.color_images).forEach((color) => {
          if (files.color_images[color]) {
            console.log(
              `DEBUG - Adding color_image_${color}:`,
              files.color_images[color],
            );
            formData.append(`color_image_${color}`, files.color_images[color]);
          }
        });
      } else {
        console.log("DEBUG - No color_images found in files object");
        console.log("DEBUG - All files keys:", Object.keys(files));
      }
 
      if (files.customization_mockups) {
        console.log(
          "DEBUG - customization mockups found:",
          Object.keys(files.customization_mockups),
        );
        Object.entries(files.customization_mockups).forEach(([position, file]) => {
          if (file) {
            const safePosition = position.replace(/\s+/g, "_").toLowerCase();
            console.log(
              `DEBUG - Adding customization_mockup_${safePosition}:`,
              file,
            );
            formData.append(`customization_mockup_${safePosition}`, file);
          }
        });
      }

      // Get auth token from localStorage with fallback support for adminToken
      const token = getAuthToken();
      const headers = {};

      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
      // Don't set Content-Type header - let browser set it with boundary

      const url = `${this.baseURL}/products`;
      console.log("FRONTEND STEP 1: Sending request to:", url);
      console.log("FRONTEND STEP 2: FormData entries:");
      for (let [key, value] of formData.entries()) {
        console.log(
          `  ${key}:`,
          value instanceof File ? `File(${value.name})` : value,
        );
      }

      const response = await fetch(url, {
        method: "POST",
        headers,
        body: formData,
      });

      console.log("FRONTEND STEP 3: Response status:", response.status);
      console.log("FRONTEND STEP 4: Response ok:", response.ok);

      // Handle 401 Unauthorized - token expired or invalid
      if (response.status === 401) {
        console.log("🔐 Authentication failed - clearing expired token");
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        // Trigger a page reload to redirect to login
        window.location.href = "/login";
        throw new Error("Authentication expired. Please login again.");
      }

      if (!response.ok) {
        // Attempt to read and log the response body for easier debugging (500/400 errors)
        let errorText = "";
        try {
          errorText = await response.text();
          console.error("FRONTEND - Error response body:", errorText);
        } catch (e) {
          console.error("FRONTEND - Failed to read error response body:", e);
        }

        throw new Error(
          `API Error: ${response.status} - ${response.statusText}` + (errorText ? ` | body: ${errorText}` : ""),
        );
      }

      return await response.json();
    } catch (error) {
      console.error("Failed to create product with files:", error);
      throw error;
    }
  }

  // Update product with files
  async updateProductWithFiles(id, productData, files) {
    try {
      console.log("🔧 UPDATE PRODUCT DEBUG:");
      console.log("🔧 Product ID:", id);
      console.log("🔧 Product Data:", productData);
      console.log("🔧 Files:", files);

      const formData = new FormData();

      // Add product data as JSON string
      formData.append("productData", JSON.stringify(productData));
      console.log("🔧 FormData - productData added");

      // Add files if they exist
      const allFiles = [];

      // Add thumbnail first (if exists)
      if (files.thumbnail) {
        allFiles.push(files.thumbnail);
        console.log("🔧 Added thumbnail to files array");
      }

      // Add additional images
      if (files.additional_images && files.additional_images.length > 0) {
        allFiles.push(...files.additional_images);
        console.log(
          "🔧 Added additional images to files array:",
          files.additional_images.length,
          "files",
        );
      }

      // Add meta image
      if (files.meta_image) {
        allFiles.push(files.meta_image);
        console.log("🔧 Added meta image to files array");
      }

      // Add all files under 'images' field (backend expects this)
      allFiles.forEach((file) => {
        formData.append("images", file);
      });

      // Also add additional_images files separately for backend processing
      if (files.additional_images && files.additional_images.length > 0) {
        files.additional_images.forEach((file, index) => {
          formData.append(`additional_image_${index}`, file);
        });
        console.log(
          "🔧 FormData - additional_image fields added:",
          files.additional_images.length,
        );
      }

      if (allFiles.length > 0) {
        console.log("🔧 FormData - images added:", allFiles.length, "files");
      }
 
      if (files.customization_mockups) {
        Object.entries(files.customization_mockups).forEach(([position, file]) => {
          if (file) {
            const safePosition = position.replace(/\s+/g, "_").toLowerCase();
            formData.append(`customization_mockup_${safePosition}`, file);
            console.log(`🔧 Added customization mockup file for ${position}`);
          }
        });
      }
 
      // Get auth token from localStorage with fallback support for adminToken
      const token = getAuthToken();
      const headers = {};

      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
        console.log("🔧 Auth token added");
      }
      // Don't set Content-Type header - let browser set it with boundary

      const url = `${this.baseURL}/products/${id}`;
      console.log("🔧 Making PATCH request to:", url);
      console.log("🔧 FormData entries:");
      for (let [key, value] of formData.entries()) {
        console.log(
          "🔧   ",
          key,
          value instanceof File ? `File: ${value.name}` : value,
        );
      }

      const response = await fetch(url, {
        method: "PATCH",
        headers,
        body: formData,
      });

      console.log("🔧 Response status:", response.status);
      console.log("🔧 Response ok:", response.ok);

      // Handle 401 Unauthorized - token expired or invalid
      if (response.status === 401) {
        console.log("🔐 Authentication failed - clearing expired token");
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        // Trigger a page reload to redirect to login
        window.location.href = "/login";
        throw new Error("Authentication expired. Please login again.");
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.log("🔧 Error response body:", errorText);
        throw new Error(
          `API Error: ${response.status} - ${response.statusText}`,
        );
      }

      return await response.json();
    } catch (error) {
      console.error("Failed to update product with files:", error);
      throw error;
    }
  }

  // Flash Deals API Methods
  async getFlashDeals(params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await this.request(
        `/flash-deals${queryString ? "?" + queryString : ""}`,
      );
      return response.data || response;
    } catch (error) {
      console.error("Failed to fetch flash deals:", error);
      throw error;
    }
  }

  async getFlashDealById(id) {
    try {
      const response = await this.request(`/flash-deals/${id}`);
      return response.data || response;
    } catch (error) {
      console.error("Failed to fetch flash deal:", error);
      throw error;
    }
  }

  async createFlashDeal(formData, imageFile) {
    try {
      const formDataToSend = new FormData();

      // Add all form fields
      Object.keys(formData).forEach((key) => {
        if (key === "products" && Array.isArray(formData[key])) {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Add image file if provided
      if (imageFile) {
        formDataToSend.append("image", imageFile);
      }

      const response = await fetch(`${this.baseURL}/flash-deals`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: formDataToSend,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create flash deal");
      }

      const result = await response.json();
      return result.data || result;
    } catch (error) {
      console.error("Failed to create flash deal:", error);
      throw error;
    }
  }

  async updateFlashDeal(id, formData, imageFile) {
    try {
      const formDataToSend = new FormData();

      // Add all form fields
      Object.keys(formData).forEach((key) => {
        if (key === "products" && Array.isArray(formData[key])) {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Add image file if provided
      if (imageFile) {
        formDataToSend.append("image", imageFile);
      }

      const response = await fetch(`${this.baseURL}/flash-deals/${id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: formDataToSend,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update flash deal");
      }

      const result = await response.json();
      return result.data || result;
    } catch (error) {
      console.error("Failed to update flash deal:", error);
      throw error;
    }
  }

  async deleteFlashDeal(id) {
    try {
      const response = await this.request(`/flash-deals/${id}`, {
        method: "DELETE",
      });
      return response;
    } catch (error) {
      console.error("Failed to delete flash deal:", error);
      throw error;
    }
  }

  async toggleFlashDealPublish(id) {
    try {
      const response = await this.request(`/flash-deals/${id}/toggle-publish`, {
        method: "PATCH",
      });
      return response.data || response;
    } catch (error) {
      console.error("Failed to toggle flash deal publish status:", error);
      throw error;
    }
  }

  async getActiveFlashDeals() {
    try {
      const response = await this.request("/flash-deals/active");
      return response.data || response;
    } catch (error) {
      console.error("Failed to fetch active flash deals:", error);
      throw error;
    }
  }

  async addProductToFlashDeal(flashDealId, productData) {
    try {
      const response = await this.request(
        `/flash-deals/${flashDealId}/products`,
        {
          method: "POST",
          body: JSON.stringify(productData),
        },
      );
      return response.data || response;
    } catch (error) {
      console.error("Failed to add product to flash deal:", error);
      throw error;
    }
  }

  async removeProductFromFlashDeal(flashDealId, productId) {
    try {
      const response = await this.request(
        `/flash-deals/${flashDealId}/products/${productId}`,
        {
          method: "DELETE",
        },
      );
      return response.data || response;
    } catch (error) {
      console.error("Failed to remove product from flash deal:", error);
      throw error;
    }
  }

  // Variant management methods

  // Generate/sync variants for a product
  async generateVariants(productId, variantOptions, basePrice) {
    try {
      const token = getAuthToken();
      const headers = {};

      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(
        `${this.baseURL}/products/${productId}/generate-variants`,
        {
          method: "PATCH",
          headers: {
            ...headers,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            variant_options: variantOptions,
            base_price: basePrice,
          }),
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Failed to generate variants:", error);
      throw error;
    }
  }

  // Get variants for a product
  async getProductVariants(productId) {
    try {
      const token = getAuthToken();
      const headers = {};

      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(
        `${this.baseURL}/products/${productId}/variants`,
        {
          method: "GET",
          headers,
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Failed to get product variants:", error);
      throw error;
    }
  }

  // Update individual variant
  async updateVariant(variantId, updateData) {
    try {
      const token = getAuthToken();
      const headers = {};

      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(
        `${this.baseURL}/products/variants/${variantId}`,
        {
          method: "PATCH",
          headers: {
            ...headers,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updateData),
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Failed to update variant:", error);
      throw error;
    }
  }
}

// Create singleton instance
const apiService = new ApiService();

export default apiService;
